import React from 'react';
import { Typography } from 'antd';
import { ReturnsPopover } from 'components/Custom/ReturnsPopover';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import { MappedReturn } from 'store/returns/models/returnData';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface HorizontalBarProps {
  label: string;
  value: number;
  maxValue: number;
  onClick?: () => void;
  returns?: MappedReturn[];
  createSubmission?: (connectionId: string, mappingId: string) => void;
}

export const HorizontalBar: React.FC<HorizontalBarProps> = ({
  label,
  value,
  maxValue,
  onClick,
  returns,
  createSubmission,
}) => {
  const { i18n } = useTranslation();
  const displayValue = formatDisplayInteger(value, i18n.language);
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;

  const barContent = (
    <div style={{ marginBottom: '16px', width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: '8px',
        }}
      >
        <Text
          ellipsis={{ tooltip: label }}
          style={{
            fontSize: '14px',
            color: 'var(--color-text-dark, #042a2a)',
            flex: 1,
            minWidth: 0,
          }}
        >
          {label}
        </Text>
        <Text
          style={{
            fontSize: '14px',
            color: 'var(--color-text-dark, #042a2a)',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {displayValue}
        </Text>
      </div>
      <div
        style={{
          width: '100%',
          height: '24px',
          backgroundColor: 'var(--color-surface, #F3F7F8)',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative',
          cursor: onClick && value > 0 ? 'pointer' : 'default',
          direction: 'ltr',
        }}
        onClick={onClick && value > 0 ? onClick : undefined}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: 'var(--color-primary)',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'width 0.3s ease',
          }}
        >
          {value > 0 && (
            <span
              style={{
                color: 'var(--color-on-primary, #052325)',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: '1',
              }}
            >
              {displayValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (returns && returns.length > 0) {
    return (
      <ReturnsPopover
        returns={returns}
        onClick={onClick}
        onCreateSubmission={createSubmission}
      >
        {barContent}
      </ReturnsPopover>
    );
  }

  return barContent;
};
